'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildPdfText,
  createCleanorExportFileName,
  createPdfPreviewUrl,
  formatFileSize,
  isPdfFile,
  openPdfDocument,
  parsePageRanges,
  triggerBrowserDownload,
  type PdfJsDocument,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

type PdfToTextSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type ExtractionProgress = {
  current: number;
  total: number;
  label: string;
};

type PageMode = 'all-pages' | 'custom';

function createTextBlob(text: string) {
  return new Blob([text], { type: 'text/plain;charset=utf-8' });
}

async function readSource(file: File): Promise<PdfToTextSource> {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      previewUrl: await createPdfPreviewUrl(document),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

function createTextFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return createCleanorExportFileName('pdf-to-text', [pageLabel], 'txt');
}

function buildCombinedText(pageNumbers: number[], pageTexts: string[]) {
  if (pageTexts.length <= 1) {
    return pageTexts[0] ?? '';
  }

  return pageTexts
    .map((pageText, index) => {
      const pageNumber = pageNumbers[index];
      return `Page ${pageNumber}\n${'-'.repeat(8)}\n${pageText || '[No text found]'}`;
    })
    .join('\n\n');
}

export default function BrowserPdfToText() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<PdfToTextSource | null>(null);
  const [pageMode, setPageMode] = useState<PageMode>('all-pages');
  const [customRanges, setCustomRanges] = useState('1-3');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [progress, setProgress] = useState<ExtractionProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [extractedText, setExtractedText] = useState('');
  const [processedPages, setProcessedPages] = useState<number | null>(null);

  const plannedPages = useMemo(() => {
    if (!source) {
      return [] as number[];
    }

    try {
      return pageMode === 'all-pages'
        ? Array.from({ length: source.pageCount }, (_, index) => index + 1)
        : parsePageRanges(customRanges, source.pageCount);
    } catch {
      return [] as number[];
    }
  }, [customRanges, pageMode, source]);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }
    };
  }, [source]);

  function replaceSource(nextSource: PdfToTextSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  function resetResultState() {
    setErrorMessage(null);
    setCreditMessage(null);
    setExtractedText('');
    setProcessedPages(null);
    setProgress(null);
  }

  async function handleFileSelection(file: File | null) {
    resetResultState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file to extract text from.');
      return;
    }

    try {
      replaceSource(await readSource(file));
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    replaceSource(null);
    resetResultState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function extractText() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    let pageNumbers: number[] = [];

    try {
      pageNumbers =
        pageMode === 'all-pages'
          ? Array.from({ length: source.pageCount }, (_, index) => index + 1)
          : parsePageRanges(customRanges, source.pageCount);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Check the page range and try again.',
      );
      return;
    }

    setIsExtracting(true);
    resetResultState();
    setProgress({ current: 0, total: pageNumbers.length, label: 'Opening PDF…' });

    let document: PdfJsDocument | null = null;

    try {
      const credits = await consumeToolUsage(
        'pdf-to-text',
        'Credits are not available for another PDF extraction right now.',
      );

      document = await openPdfDocument(new Uint8Array(await source.file.arrayBuffer()));
      const pageTexts: string[] = [];

      for (let index = 0; index < pageNumbers.length; index += 1) {
        const pageNumber = pageNumbers[index];
        setProgress({
          current: index + 1,
          total: pageNumbers.length,
          label: `Reading text from page ${pageNumber}…`,
        });

        const page = await document.getPage(pageNumber);

        try {
          const textContent = await page.getTextContent?.();
          const pageText = textContent ? buildPdfText(textContent.items) : '';
          pageTexts.push(pageText);
        } finally {
          page.cleanup?.();
        }
      }

      const nextText = buildCombinedText(pageNumbers, pageTexts);
      setExtractedText(nextText);
      setProcessedPages(pageNumbers.length);
      setCreditMessage(
        nextText
          ? formatToolCreditSuccess('Text extraction', credits)
          : 'No selectable text was found. Try OCR PDF to Text if this is a scanned document.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Text could not be extracted from this PDF.',
      );
    } finally {
      setProgress(null);
      setIsExtracting(false);
      document?.cleanup?.();
      await document?.destroy?.();
    }
  }

  async function copyText() {
    if (!extractedText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(extractedText);
      setCreditMessage('Text copied to clipboard.');
      setErrorMessage(null);
    } catch {
      setErrorMessage('Clipboard access is blocked in this browser.');
    }
  }

  function downloadText() {
    if (!extractedText) {
      return;
    }

    const blob = createTextBlob(extractedText);
    const nextUrl = URL.createObjectURL(blob);
    triggerBrowserDownload(nextUrl, createTextFileName(processedPages ?? source?.pageCount ?? 1));
    URL.revokeObjectURL(nextUrl);
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Extract selectable text from one PDF</h2>
          <p>
            Open a text-based PDF in the browser, pull out the copyable text, and download or copy
            the result without sending the file to a server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? 'PDF ready for text extraction' : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${source.pageCount} pages • ${formatFileSize(source.size)}`
                  : 'Best for PDFs that already contain selectable text'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls tool-pdf-settings">
            <label className="tool-live-route">
              <span>Page mode</span>
              <select
                onChange={(event) => setPageMode(event.target.value as PageMode)}
                value={pageMode}
              >
                <option value="all-pages">All pages</option>
                <option value="custom">Custom pages</option>
              </select>
            </label>

            {pageMode === 'custom' ? (
              <label className="tool-live-route">
                <span>Pages</span>
                <input
                  onChange={(event) => setCustomRanges(event.target.value)}
                  placeholder="1-3, 5, 7"
                  value={customRanges}
                />
              </label>
            ) : null}
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isExtracting}
              onClick={() => void extractText()}
              type="button"
            >
              {isExtracting ? 'Extracting text…' : 'Extract text'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-pdf-progress">
              <span>{progress.label}</span>
              <progress max={progress.total} value={progress.current} />
            </div>
          ) : null}
          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Direct PDF text extraction</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → Text</span>
            <span>No OCR</span>
            <span>Copy ready</span>
            <span>Local export</span>
          </div>
          <p>
            Best for PDFs that already have selectable text. If this is a scan, use OCR PDF to Text
            instead.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Use a standard unlocked PDF file. Password-protected files are not supported here.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              <div>
                <strong>Planned pages</strong>
                <span>{plannedPages.length || 'Check range'}</span>
              </div>
              {processedPages ? (
                <div>
                  <strong>Last pass</strong>
                  <span>{processedPages} pages</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>Source preview</h3>
          <div className="tool-live-preview-frame">
            <img alt="" src={source.previewUrl} />
          </div>
        </div>
      ) : null}

      {source ? (
        <div className="tool-live-preview tool-ocr-output-shell">
          <div className="tool-live-header">
            <div>
              <h3>Extracted text</h3>
              <p>Review the direct text output, then copy it or download it as TXT.</p>
            </div>
            {extractedText ? (
              <div className="hero-actions tool-live-actions">
                <button
                  className="button button-ghost"
                  onClick={() => void copyText()}
                  type="button"
                >
                  Copy text
                </button>
                <button className="button button-ghost" onClick={downloadText} type="button">
                  Download TXT
                </button>
              </div>
            ) : null}
          </div>
          <textarea
            className="tool-ocr-output"
            placeholder="Run extraction to see text here."
            readOnly
            value={extractedText}
          />
        </div>
      ) : null}
    </article>
  );
}
